<script setup lang="ts">
  import { ref, watch } from 'vue'
  import QRCodeVue from 'qrcode.vue'
  import { Download, Copy } from 'lucide-vue-next'
  import { Button } from '@/components/ui/button'
  import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription
  } from '@/components/ui/dialog'
  import { useToast } from '@/components/ui/toast'

  const props = defineProps<{
    modelValue: boolean
    url: string
    title?: string
    description?: string
    copyButtonText?: string
    saveButtonText?: string
    copiedTitle?: string
    copiedDescription?: string
    copyErrorTitle?: string
    copyErrorDescription?: string
  }>()

  const emit = defineEmits<{
    'update:modelValue': [value: boolean]
  }>()

  const { toast } = useToast()
  const qrCodeRef = ref<HTMLElement>()

  const close = () => {
    emit('update:modelValue', false)
  }

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(props.url)
      toast({
        title: props.copiedTitle || 'Link kopiert',
        description: props.copiedDescription || 'Der Link wurde in die Zwischenablage kopiert',
        variant: 'default'
      })
    } catch (error) {
      toast({
        title: props.copyErrorTitle || 'Fehler',
        description: props.copyErrorDescription || 'Der Link konnte nicht kopiert werden',
        variant: 'destructive'
      })
    }
  }

  const downloadQRCode = () => {
    const canvas = qrCodeRef.value?.querySelector('canvas')
    if (canvas) {
      const url = (canvas as HTMLCanvasElement).toDataURL('image/png')
      const link = document.createElement('a')
      link.download = `qrcode-${Date.now()}.png`
      link.href = url
      link.click()
    }
  }
</script>

<template>
  <Dialog
    :open="modelValue"
    @update:open="close"
  >
    <DialogContent class="sm:max-w-md">
      <DialogHeader>
        <DialogTitle>{{ title || 'QR Code' }}</DialogTitle>
        <DialogDescription>
          {{ description || 'Scannen Sie diesen QR Code, um direkt zum Fragebogen zu gelangen' }}
        </DialogDescription>
      </DialogHeader>

      <div class="flex flex-col items-center space-y-4">
        <div
          ref="qrCodeRef"
          class="rounded-lg border border-gray-200 bg-white p-4"
        >
          <QRCodeVue
            :value="url"
            :size="250"
            level="M"
            render-as="canvas"
            :margin="1"
          />
        </div>

        <div class="w-full rounded-lg border border-gray-200 bg-gray-50 p-3">
          <p class="break-all text-sm text-gray-600">{{ url }}</p>
        </div>

        <div class="flex w-full gap-2">
          <Button
            variant="secondary"
            class="flex-1"
            @click="copyLink"
          >
            <Copy class="mr-2 h-4 w-4" />
            {{ copyButtonText || 'Link kopieren' }}
          </Button>
          <Button
            variant="default"
            class="flex-1"
            @click="downloadQRCode"
          >
            <Download class="mr-2 h-4 w-4" />
            {{ saveButtonText || 'QR Code speichern' }}
          </Button>
        </div>
      </div>
    </DialogContent>
  </Dialog>
</template>
